import { useEffect } from "react";

import { getPublicSettings } from "@/lib/leads.functions";

export type TrackingIds = {
  gtmId: string | null;
  ga4Id: string | null;
  googleAdsId: string | null;
  clarityId: string | null;
};

function injectScript(id: string, code: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.innerHTML = code;
  document.head.appendChild(script);
}

function injectSrc(id: string, src: string) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

/**
 * Injeta GTM, GA4, Google Ads e Microsoft Clarity apenas quando os IDs
 * estiverem cadastrados no painel administrativo.
 */
export function Tracking({ ids }: { ids: TrackingIds }) {
  useEffect(() => {
    if (typeof window === "undefined") return;

    let done = false;

    const events = ["pointerdown", "touchstart", "keydown", "scroll", "mousemove"] as const;

    const cleanup = () => {
      for (const evt of events) window.removeEventListener(evt, load);
    };

    const load = () => {
      if (done) return;
      done = true;
      cleanup();
      // Valores do painel administrativo têm prioridade; se o banco não
      // responder, seguem os IDs fixos no código.
      void getPublicSettings()
        .then((remote) => inject(remote ?? ids))
        .catch(() => inject(ids));
    };

    const inject = (ids: TrackingIds) => {
    if (ids.gtmId) {
      injectScript(
        "gtm-init",
        `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${ids.gtmId}');`,
      );
    }

    const gtagId = ids.ga4Id || ids.googleAdsId;
    if (gtagId) {
      injectSrc("gtag-src", `https://www.googletagmanager.com/gtag/js?id=${gtagId}`);
      injectScript(
        "gtag-init",
        `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());${
          ids.ga4Id ? `gtag('config','${ids.ga4Id}');` : ""
        }${ids.googleAdsId ? `gtag('config','${ids.googleAdsId}');` : ""}`,
      );
    }

    if (ids.clarityId) {
      injectScript(
        "clarity-init",
        `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,"clarity","script","${ids.clarityId}");`,
      );
    }
    };

    // As tags entram somente na primeira interação real do visitante
    // (toque, rolagem, clique ou movimento do mouse), liberando a thread
    // principal durante a pintura inicial.
    for (const evt of events) window.addEventListener(evt, load, { once: true, passive: true });

    return cleanup;
  }, [ids.clarityId, ids.ga4Id, ids.googleAdsId, ids.gtmId]);

  return null;
}
